import { HTTP_STATUS } from "./constants/httpStatus";
import {
  AIRPLANE_ACCESS_UNAUTHORIZED_REASON,
  AIRPLANE_NOT_FOUND_REASON,
  LOGIN_INVALID_CREDENTIALS_REASON,
  NOT_FOUND_MESSAGE,
  ORGANIZATION_ACCESS_CODE_NOT_FOUND_REASON,
} from "./constants/responseMessages";

type ApiErrorShape = {
  code?: number | string;
  message?: string;
  reason?: string;
  status?: number;
  statusCode?: number;
};

function hasStatusCode(error: ApiErrorShape, statusCode: number): boolean {
  return (
    error.status === statusCode ||
    error.statusCode === statusCode ||
    error.code === statusCode
  );
}

function isNotFoundApiError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const apiError = error as ApiErrorShape;

  if (hasStatusCode(apiError, HTTP_STATUS.NOT_FOUND)) {
    return true;
  }

  // Fallback for legacy/inconsistent API error shapes that only expose text fields.
  // This keeps "not found" detection stable across endpoints.
  const hasNotFoundMessage =
    apiError.reason === NOT_FOUND_MESSAGE.reason ||
    apiError.message === NOT_FOUND_MESSAGE.reason ||
    apiError.reason === AIRPLANE_NOT_FOUND_REASON ||
    apiError.message === AIRPLANE_NOT_FOUND_REASON ||
    apiError.reason === ORGANIZATION_ACCESS_CODE_NOT_FOUND_REASON ||
    apiError.message === ORGANIZATION_ACCESS_CODE_NOT_FOUND_REASON;
  return hasNotFoundMessage;
}

const LOGIN_GENERIC_ERROR_MESSAGE =
  "Something went wrong. Please try again or contact support." as const;

function isLoginCredentialsError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const apiError = error as ApiErrorShape;

  return (
    isNotFoundApiError(error) ||
    apiError.reason === LOGIN_INVALID_CREDENTIALS_REASON
  );
}

function getLoginErrorMessage(error: unknown): string {
  if (isLoginCredentialsError(error)) {
    const { reason } = error as ApiErrorShape;
    return reason ?? LOGIN_INVALID_CREDENTIALS_REASON;
  }

  if (error && typeof error === "object") {
    const { reason } = error as ApiErrorShape;
    if (reason) {
      return reason;
    }
  }

  return LOGIN_GENERIC_ERROR_MESSAGE;
}

function isUnauthorizedApiError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const apiError = error as ApiErrorShape;
  if (hasStatusCode(apiError, HTTP_STATUS.UNAUTHORIZED)) {
    return true;
  }
  // openapi-fetch throws the response JSON body only; HTTP status is not on this object.
  return (
    apiError.reason === AIRPLANE_ACCESS_UNAUTHORIZED_REASON ||
    apiError.message === AIRPLANE_ACCESS_UNAUTHORIZED_REASON
  );
}

export {
  getLoginErrorMessage,
  isLoginCredentialsError,
  isNotFoundApiError,
  isUnauthorizedApiError,
  LOGIN_GENERIC_ERROR_MESSAGE,
};
